import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, Collapse, Divider, IconButton } from "@mui/material";
import ReadMoreButton from "../../shared/components/ReadMoreButton";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import { apiRequest } from "../../shared/api/apiClient";
import LoadingSpinner from "../../shared/components/LoadingSpinner";
import { useParams } from "react-router-dom";

export default function AnalyzeResultsSccmec() {
    const { id } = useParams();
    const token = process.env.REACT_APP_ATLAS_TOKEN;

    const [sccmecData, setSccmecData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!id || !token) return;

            setLoading(true);

            try {
                const params = new URLSearchParams({
                    study_id: id,   // 👈 cambia a run_id si tu backend lo usa así
                    token,
                });

                const endpoint = `sccmec.php?${params.toString()}`;

                const data = await apiRequest(endpoint, {
                    method: "GET",
                });

                if (!cancelled) {
                    setSccmecData(data?.samples ?? []);
                }

            } catch (e) {
                if (!cancelled) console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };

    }, [id, token]);

    return (
        <Box sx={{ width: "100%", mx: "auto" }}>
            {/* Title block */}
            <Box sx={{ width: "100%", mb: 6 }}>
                <Typography variant="h4">Chromosomal cassettes</Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                    <Box
                        sx={{
                            width: 14,
                            height: 28,
                            borderTopRightRadius: 999,
                            borderBottomRightRadius: 999,
                            background: "#20BC71",
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="h5">sccmec</Typography>
                </Box>
            </Box>

            {/* Intro grid */}
            <Grid container spacing={4} sx={{ width: "100%", mb: 10 }}>
                {/* Left column */}
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: "column",
                            height: "100%",
                            maxWidth: 760,
                        }}
                    >
                        <Box sx={{ mb: 5 }}>
                            <Typography variant="body1">These results were generated by sccmec.</Typography>
                            <Typography variant="body1">
                                To read the documentation please visit the official website.
                            </Typography>
                        </Box>

                        <ReadMoreButton href={"https://github.com"} />
                    </Box>
                </Grid>

                {/* Right column (command block) */}
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}
                >
                    <Box
                        sx={{
                            background: "#000",
                            color: "#fff",
                            width: "100%",
                            maxWidth: 460,
                            minWidth: { xs: "auto", md: 400 },
                            minHeight: 120,
                            borderRadius: 4,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 2,
                        }}
                    >
                        <Box
                            sx={{
                                textAlign: "left",
                                wordBreak: "break-word",
                                fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                            }}
                        >
                            <Typography variant="body2">{"sccmec --input ${spades_scaffold}"}</Typography>
                            <Typography variant="body2">{"--outdir sccmec/${sample}/"}</Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Loading / Samples */}
            {loading ? (
                <Box
                    sx={{
                        width: "100%",
                        display: "grid",
                        placeItems: "center",
                        mt: 6,
                        gap: 2,
                    }}
                >
                    <LoadingSpinner size={170} barWidth={18} barHeight={48} />
                    <Typography sx={{ fontSize: 40 }}>Processing files</Typography>
                </Box>
            ) : (
                sccmecData?.map((sample, key) => <SccmecSample sample={sample} key={key} />)
            )}
        </Box>
    );
}

const SccmecSample = ({ sample }) => {
    const [antimicrobialData, setAntimicrobialData] = useState([]);
    const [geneData, setGeneData] = useState([]);
    const [openSample, setOpenSample] = useState(false);

    // ✅ aquí estaba el bug: era useState por error
    useEffect(() => {
        setGeneData(sample?.tables?.by_gene?.rows ?? []);
        setAntimicrobialData(sample?.tables?.by_antimicrobial_class?.rows ?? []);
    }, [sample]);

    return (
        <Box sx={{ width: "100%" }}>
            {/* Sample header */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    cursor: "pointer",
                }}
                onClick={() => setOpenSample((v) => !v)}
            >
                <Box sx={{ width: "100%" }}>
                    <Typography variant="h6">Sample {sample?.sample_id}</Typography>
                </Box>

                <Box sx={{ width: "10%", display: "flex", justifyContent: "flex-end" }}>
                    <IconButton
                        sx={{
                            maxWidth: '50px',
                            width: "40px",
                            aspectRatio: '1/1',
                            border: "1px solid",
                            borderColor: "divider",
                            flexShrink: 0,
                        }}

                        aria-label={openSample ? "Collapse" : "Expand"}
                    >
                        {openSample ? (
                            <ExpandLessRoundedIcon sx={{ width: "20px", height: "20px" }} />
                        ) : (
                            <ExpandMoreRoundedIcon sx={{ width: "20px", height: "20px" }} />
                        )}
                    </IconButton>
                </Box>
            </Box>

            <Collapse in={openSample} timeout={240} unmountOnExit>
                <Divider sx={{ borderColor: "#20BC71" }} />

                {/* By gene */}
                <Box sx={{ width: "100%", py: 1 }}>
                    <Typography variant="subtitle1">By gene</Typography>
                </Box>
                <Divider sx={{ borderColor: "#20BC71", mb: 1 }} />

                <Box sx={{ pb: 3 }}>
                    {/* <ResistanceGenesTable rows={geneData} /> */}
                </Box>

                {/* By antimicrobial class */}
                <Box sx={{ width: "100%", py: 1 }}>
                    <Typography variant="subtitle1">By antimicrobial class</Typography>
                </Box>
                <Divider sx={{ borderColor: "#20BC71", mb: 2 }} />

                <Box sx={{ width: "100%", maxWidth: 760, mb: 1 }}>
                    <Typography variant="body1">
                        The phenotype "No resistance" should be interpreted with caution, as it only means that
                        nothing in the used database indicate resistance, but resistance could exist from
                        "unknown" or not yet implemented sources.
                    </Typography>

                    <Typography variant="body1" sx={{ mt: 1 }}>
                        The "Match" column stores one of the integers 0, 1, 2, 3.
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                            <li>0: No match found</li>
                            <li>1: Match &lt; 100% ID AND match length &lt; ref length</li>
                            <li>2: Match = 100% ID AND match length &lt; ref length</li>
                            <li>3: Match = 100% ID AND match length = ref length</li>
                        </ul>
                    </Box>

                    <Typography variant="body1" sx={{ mt: 1 }}>
                        If several hits causing the same resistance are found, the highest number will be stored
                        in the "Match" column.
                    </Typography>
                </Box>

                <Box sx={{ pb: 3 }}>
                    {/* <PredictedResistanceTable rows={antimicrobialData} /> */}
                </Box>
            </Collapse>

            <Divider sx={{ borderColor: "#20BC71" }} />
        </Box>
    );
};